import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react'
import { EmptyState } from '@/components/empty-state'
import { Panel } from '@/components/panel'
import { ScoreMeter } from '@/components/score-meter'
import { Button } from '@/components/ui/button'
import { type BacklinkLink, type DomainDetail, useReferringDomain } from '@/lib/backlinks'
import { formatCount, formatDate, formatExact, shortenUrl } from '@/lib/format'
import { cn } from '@/lib/utils'
import { BACK_LINK, DetailHeader, TableSkeleton, useSiteScope } from '../-section'

export const Route = createFileRoute('/_app/$org/$host/backlinks/$domainId')({
  component: ReferringDomainPage,
})

/**
 * One domain that links here.
 *
 * A row opens a page rather than unfolding, the same rule the keyword and page
 * tables follow — and here the reason is that the interesting half is the list
 * of individual links, which is a table of its own and will not fit in a row.
 */
function ReferringDomainPage() {
  const { org, host, site } = useSiteScope()
  const { domainId } = Route.useParams()
  const { data, isPending, error } = useReferringDomain(site, Number(domainId))

  return (
    <>
      <DetailHeader
        back={
          <Link to="/$org/$host/backlinks" params={{ org, host }} className={BACK_LINK}>
            <ArrowLeftIcon className="size-3" aria-hidden />
            Backlinks
          </Link>
        }
        title={data?.domain.domain ?? '…'}
        actions={
          data && (
            <a
              href={`https://${data.domain.domain}`}
              target="_blank"
              rel="noreferrer nofollow"
              className="mono-label inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              Open
              <ExternalLinkIcon className="size-3" aria-hidden />
            </a>
          )
        }
      />

      {error ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="Domain not found"
            description={`No referring domain with that id links to ${host}.`}
            action={
              <Button asChild size="sm" variant="outline">
                <Link to="/$org/$host/backlinks" params={{ org, host }}>
                  Back to backlinks
                </Link>
              </Button>
            }
          />
        </div>
      ) : isPending || !data ? (
        <TableSkeleton />
      ) : (
        <DomainBody data={data} org={org} host={host} />
      )}
    </>
  )
}

function DomainBody({ data, org, host }: { data: DomainDetail; org: string; host: string }) {
  const { domain, links, config } = data

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Panel title="This domain">
          <dl className="space-y-3 text-[13px]">
            {/* The two figures a reader has nothing to measure against, so each
                is drawn against its own ceiling rather than left as a number. */}
            <Row label="Authority" value={<ScoreMeter value={domain.rank} kind="authority" />} />
            <Row label="Spam score" value={<ScoreMeter value={domain.spamScore} kind="spam" />} />
            <Row label="Platform" value={domain.platformTypes.join(', ') || '—'} mono={false} />
            <Row label="Suffix" value={domain.tld ?? '—'} />
          </dl>
        </Panel>

        <Panel title="Links to this site">
          <dl className="space-y-3 text-[13px]">
            <Row label="Links" value={formatExact(domain.backlinks)} />
            <Row
              label="Linking pages"
              value={
                domain.dofollowPages === null
                  ? formatExact(domain.referringPages)
                  : `${formatExact(domain.referringPages)} (${formatCount(domain.dofollowPages)} followed)`
              }
            />
            <Row
              label="Pointing at dead urls"
              value={formatExact(domain.brokenBacklinks)}
              tone={domain.brokenBacklinks > 0 ? 'bad' : undefined}
            />
            {/* The provider's own dates: when its crawler first saw a link and,
                if the domain has gone, when it stopped seeing one. Neither is
                when the link was placed or removed, and nothing here says it is. */}
            <Row label="First seen" value={domain.firstSeen ? formatDate(domain.firstSeen) : '—'} />
            {domain.lostAt && (
              <Row label="Last seen" value={formatDate(domain.lostAt)} tone="bad" />
            )}
          </dl>
        </Panel>
      </div>

      <Panel
        title="Individual links"
        meta={links.total > 0 ? formatExact(links.total) : undefined}
        flush
      >
        {links.rows.length === 0 ? (
          <div className="px-4 py-6 text-[13px] text-muted-foreground">
            {config?.linkLevelEnabled
              ? 'No individual link from this domain has been collected yet.'
              : 'Only broken links are held for this site, so there is nothing else to list here.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {links.rows.map((link) => (
              <LinkRow key={link.id} link={link} org={org} host={host} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}

function LinkRow({ link, org, host }: { link: BacklinkLink; org: string; host: string }) {
  return (
    <li className="space-y-1 px-4 py-3 text-[12.5px]">
      <div className="flex items-baseline gap-2">
        <a
          href={link.urlFrom}
          target="_blank"
          rel="noreferrer nofollow"
          className="min-w-0 flex-1 truncate font-mono text-link underline-offset-2 hover:underline"
          title={link.urlFrom}
        >
          {link.urlFrom.replace(/^https?:\/\//, '')}
        </a>
        {/* Both states are muted on purpose: nofollow is not a fault, and a
            warning tint here would read as one on half of every healthy profile. */}
        <span className="mono-label shrink-0 rounded-sm bg-secondary px-1 py-px text-muted-foreground">
          {link.dofollow ? 'follow' : 'nofollow'}
        </span>
        {link.isBroken && (
          <span className="mono-label shrink-0 rounded-sm bg-destructive/10 px-1 py-px text-destructive">
            {link.urlToStatusCode ?? 'broken'}
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-2 text-muted-foreground">
        <span className="shrink-0 font-mono text-[11px]">→</span>
        {link.pageId ? (
          <Link
            to="/$org/$host/pages/$pageId"
            params={{ org, host, pageId: String(link.pageId) }}
            className="min-w-0 flex-1 truncate font-mono text-[11.5px] underline-offset-2 hover:text-foreground hover:underline"
            title={link.urlTo}
          >
            {shortenUrl(link.urlTo, host)}
          </Link>
        ) : (
          <span className="min-w-0 flex-1 truncate font-mono text-[11.5px]" title={link.urlTo}>
            {shortenUrl(link.urlTo, host)}
          </span>
        )}
        {link.firstSeen && (
          <span className="shrink-0 font-mono text-[11px] tabular">
            {formatDate(link.firstSeen)}
          </span>
        )}
      </div>

      {link.anchor && (
        <p className="truncate text-[12px] text-muted-foreground" title={link.anchor}>
          “{link.anchor}”
        </p>
      )}
    </li>
  )
}

function Row({
  label,
  value,
  mono = true,
  tone,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
  tone?: 'bad'
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="mono-label shrink-0 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'min-w-0 truncate text-right',
          mono && 'font-mono tabular',
          tone === 'bad' && 'text-destructive',
        )}
      >
        {value}
      </dd>
    </div>
  )
}
